import { useAuthStore } from '../auth/store';

const BASE_URL = import.meta.env.VITE_API_BASE_URL ?? 'http://localhost:3000/v1';

export interface ApiError extends Error {
  status: number;
  code?: string;
  data?: unknown;
}

function buildError(message: string, status: number, data?: unknown, code?: string): ApiError {
  const e = new Error(message) as ApiError;
  e.status = status;
  e.data = data;
  e.code = code;
  return e;
}

interface RequestOpts {
  method?: 'GET' | 'POST' | 'PUT' | 'PATCH' | 'DELETE';
  body?: unknown;
  query?: Record<string, string | number | undefined | null>;
  signal?: AbortSignal;
}

export async function apiRequest<T>(path: string, opts: RequestOpts = {}): Promise<T> {
  const { method = 'GET', body, query, signal } = opts;
  const token = useAuthStore.getState().token;

  const url = new URL(`${BASE_URL}${path}`);
  if (query) {
    for (const [k, v] of Object.entries(query)) {
      if (v === undefined || v === null || v === '') continue;
      url.searchParams.set(k, String(v));
    }
  }

  const headers: Record<string, string> = { 'Content-Type': 'application/json' };
  if (token) headers.Authorization = `Bearer ${token}`;

  const res = await fetch(url.toString(), {
    method,
    headers,
    body: body !== undefined ? JSON.stringify(body) : undefined,
    signal,
  });

  let parsed: unknown = null;
  const text = await res.text();
  if (text) {
    try {
      parsed = JSON.parse(text);
    } catch {
      parsed = text;
    }
  }

  if (!res.ok) {
    if (res.status === 401) {
      if (!path.includes('/admin/auth/login')) {
        useAuthStore.getState().clear();
        if (typeof window !== 'undefined' && window.location.pathname !== '/login') {
          window.location.href = '/login';
        }
      }
    }
    const message =
      (typeof parsed === 'object' && parsed && 'message' in parsed
        ? Array.isArray((parsed as any).message)
          ? (parsed as any).message.join(', ')
          : String((parsed as any).message)
        : null) ?? `Request failed: ${res.status}`;
    const code =
      typeof parsed === 'object' && parsed && 'code' in parsed
        ? String((parsed as any).code)
        : undefined;
    throw buildError(message, res.status, parsed, code);
  }

  return parsed as T;
}
